import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

export interface Permission {
  id: string;
  name: string;
  category: string;
  categoryKey?: string;
}

interface PermissionsSelectorProps {
  permissions: Permission[];
  selectedIds: string[];
  onToggle: (permissionId: string) => void;
  disabled?: boolean;
  className?: string;
  defaultExpanded?: boolean;
}

function formatPermissionLabel(name: string): string {
  const parts = name.split('.');
  if (parts.length < 2) return name;
  const [resource, action] = parts;
  const actionLabel = action === 'read' ? 'View' : action === 'write' ? 'Edit' : action;
  const resourceLabel = resource.charAt(0).toUpperCase() + resource.slice(1);
  return `${resourceLabel} – ${actionLabel}`;
}

export function PermissionsSelector({
  permissions,
  selectedIds,
  onToggle,
  disabled = false,
  className,
  defaultExpanded = false,
}: PermissionsSelectorProps) {
  const byCategory = permissions.reduce((acc, perm) => {
    const key = perm.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);
  const categories = Object.keys(byCategory).sort();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    defaultExpanded ? new Set(categories) : new Set()
  );

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const expandAll = () => setExpandedCategories(new Set(categories));
  const collapseAll = () => setExpandedCategories(new Set());

  return (
    <div className={cn('space-y-0 border rounded-md', className)}>
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <span className="text-sm font-medium">Permissions</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={expandAll}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Collapse all
          </button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {categories.map((category) => {
          const perms = byCategory[category];
          const isExpanded = expandedCategories.has(category);
          const selectedInCategory = perms.filter((p) => selectedIds.includes(p.id)).length;

          return (
            <div key={category} className="border-b last:border-b-0">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className="font-medium text-sm flex-1">{category}</span>
                <span className="text-xs text-muted-foreground">
                  {selectedInCategory}/{perms.length}
                </span>
              </button>
              <div
                className={cn(
                  'transition-all duration-200',
                  isExpanded ? 'block' : 'hidden'
                )}
              >
                <div className="pl-6 pr-3 pb-3 space-y-2">
                  {perms.map((perm) => (
                    <label
                      key={perm.id}
                      className={cn(
                        'flex items-center gap-2 text-sm cursor-pointer py-1.5 rounded px-2 hover:bg-muted/30',
                        disabled && 'cursor-not-allowed opacity-60'
                      )}
                    >
                      <Checkbox
                        checked={selectedIds.includes(perm.id)}
                        onCheckedChange={() => !disabled && onToggle(perm.id)}
                        disabled={disabled}
                      />
                      <span>{formatPermissionLabel(perm.name)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PermissionsSelector;
